import React from "react";
import { useQuery } from "@tanstack/react-query";
import { UserCheck, Robot, PencilSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function JobSessions({ jobId, machines }) {
  const { data, isLoading } = useQuery({
    queryKey: ["job-sessions", jobId],
    queryFn: async () => (await api.get("/work/sessions", { params: { job_id: jobId } })).data,
  });

  if (isLoading) return <div className="p-5 text-sm text-slate-400">Loading timers...</div>;
  const rows = data || [];
  if (rows.length === 0) {
    return <div className="p-5 text-sm text-slate-400" data-testid="job-sessions-empty">No timers recorded on this job yet.</div>;
  }

  const machineName = (id) => (machines || []).find((m) => m.id === id)?.code || "—";
  const onBehalf = rows.filter((r) => r.started_on_behalf).length;

  return (
    <div data-testid="job-sessions">
      {onBehalf > 0 && (
        <div className="px-5 pt-4 text-xs text-amber-700" data-testid="job-sessions-onbehalf-note">
          {onBehalf} of {rows.length} timer{rows.length === 1 ? "" : "s"} were started by a manager on the operator's behalf.
        </div>
      )}
      <div className="p-4 sm:p-5 overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Session</TableHead><TableHead>Operator</TableHead><TableHead>Machine</TableHead>
              <TableHead>Start</TableHead><TableHead>End</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              <TableHead>Started by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} data-testid={`job-session-${s.code}`}>
                <TableCell className="font-mono text-xs">{s.code}</TableCell>
                <TableCell className="font-medium">{s.operator_name}</TableCell>
                <TableCell>{machineName(s.machine_id)}</TableCell>
                <TableCell className="whitespace-nowrap text-slate-600">{fmt(s.start_time)}</TableCell>
                <TableCell className="whitespace-nowrap text-slate-600">
                  {s.end_time ? fmt(s.end_time) : <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">{s.status}</Badge>}
                </TableCell>
                <TableCell className="text-right tabular-nums">{s.actual_hours}</TableCell>
                <TableCell>
                  <span className="flex flex-wrap items-center gap-1.5">
                    {s.auto_started ? (
                      <Badge variant="outline" className="bg-slate-100 text-slate-600" data-testid={`session-auto-${s.code}`}>
                        <Robot className="h-3.5 w-3.5 mr-1" /> auto-started
                      </Badge>
                    ) : s.started_on_behalf ? (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`session-onbehalf-${s.code}`}>
                        <UserCheck className="h-3.5 w-3.5 mr-1" weight="bold" /> {s.started_by}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-500">{s.started_by || s.operator_name}</span>
                    )}
                    {s.time_edited_by && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid={`session-edited-${s.code}`}>
                        <PencilSimple className="h-3.5 w-3.5 mr-1" /> time corrected
                      </Badge>
                    )}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
